import API_URL from '../api/config.js';

const CACHE_KEY = 'tradeable_assets_cache_v2';
const CACHE_TIMESTAMP_KEY = 'tradeable_assets_cache_ts_v2';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

/**
 * Get tradeable assets from cache or fetch from server.
 * Caches the full list in localStorage with a 24h TTL.
 */
export async function getTradeableAssets(forceRefresh = false) {
  if (!forceRefresh) {
    const cached = getCachedAssets();
    if (cached) return cached;
  }

  const assets = await fetchAssetsFromServer();
  setCachedAssets(assets);
  return assets;
}

function getCachedAssets() {
  try {
    const ts = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!ts) return null;

    const age = Date.now() - parseInt(ts, 10);
    if (age > CACHE_TTL_MS) return null;

    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setCachedAssets(assets) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(assets));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, String(Date.now()));
  } catch (e) {
    console.error('Failed to cache tradeable assets:', e);
  }
}

async function fetchAssetsFromServer() {
  const response = await fetch(`${API_URL}/assets`, {
    headers: getAuthHeaders(),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch tradeable assets');
  }
  return data.data;
}

/**
 * Fetch the user's recent assets (sorted by last_used_at desc).
 */
export async function getRecentAssets() {
  const response = await fetch(`${API_URL}/assets/recent`, {
    headers: getAuthHeaders(),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch recent assets');
  }
  return data.data;
}

/**
 * Track a symbol as recently used.
 */
export async function trackRecentAsset(symbol) {
  try {
    await fetch(`${API_URL}/assets/recent`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ symbol }),
    });
  } catch (e) {
    // Non-critical, don't throw
    console.error('Failed to track recent asset:', e);
  }
}

/**
 * Clear the tradeable assets cache (e.g., on logout).
 */
export function clearAssetsCache() {
  localStorage.removeItem(CACHE_KEY);
  localStorage.removeItem(CACHE_TIMESTAMP_KEY);
}
